/**
 * Offline/online tile config for optional MapLibre embed.
 * Provider-agnostic: set VITE_MAP_STYLE_URL, or a tiles provider + key.
 */

import {
  inferMapTilesProvider,
  resolveTileStyleUrl,
  type MapTilesProviderId,
} from './tileStyleProviders';

export type MapTilesConfig = {
  provider: MapTilesProviderId;
  styleUrl: string | null;
  apiKey: string | null;
  /** Offline vector pack URL (any PMTiles host). */
  pmtilesUrl: string | null;
  /** Rough bbox for Greater Havana offline pack documentation / download */
  offlineRegion: {
    id: string;
    name: string;
    west: number;
    south: number;
    east: number;
    north: number;
  };
  enabled: boolean;
};

/** Default offline planning region: Greater Havana (WGS84). */
export const HAVANA_OFFLINE_REGION = {
  id: 'greater_havana',
  name: 'Greater Havana',
  west: -82.55,
  south: 22.95,
  east: -82.15,
  north: 23.25,
} as const;

export function readMapTilesConfig(env: Record<string, string | undefined> = {}): MapTilesConfig {
  const styleUrlRaw = (env.VITE_MAP_STYLE_URL || env.VITE_MAPTILER_STYLE_URL || '').trim() || null;
  const apiKey = (env.VITE_MAP_API_KEY || env.VITE_MAPTILER_KEY || '').trim() || null;
  const pmtilesUrl = (env.VITE_MAP_PMTILES_URL || env.VITE_MAP_OFFLINE_PACK_URL || '').trim() || null;
  const provider = inferMapTilesProvider(env);
  const resolvedStyle = resolveTileStyleUrl(provider, {
    apiKey,
    styleUrl: styleUrlRaw,
    pmtilesUrl,
  });
  return {
    provider,
    styleUrl: resolvedStyle,
    apiKey,
    pmtilesUrl,
    offlineRegion: { ...HAVANA_OFFLINE_REGION },
    enabled: Boolean(resolvedStyle),
  };
}

export function mapTilesFromImportMeta(): MapTilesConfig {
  try {
    const env = (import.meta as { env?: Record<string, string | undefined> }).env ?? {};
    return readMapTilesConfig(env);
  } catch {
    return readMapTilesConfig({});
  }
}
